import { Badge, Button, Modal, Spinner } from "react-bootstrap";

import { useGetTeamById } from "../../../../../../api/jeep-rally/teams/teams.hooks";
import { getJeepRallyMediaUrl } from "../../../../../../api/jeep-rally/api";
import {
  formatLabel,
  formatRegistrationDate,
  getEntityId,
  getRegistrationApiErrorMessage,
} from "../utils/competitors.utils";

const DetailRow = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-muted small mb-1">{label}</div>
    <div>{value || "-"}</div>
  </div>
);

const resolvePersonName = (person) => {
  if (!person) return "-";
  if (typeof person === "string") return person;
  return person.name || person.full_name || person.email || getEntityId(person) || "-";
};

const TeamDetailModal = ({ show, teamId, fallback = null, onClose }) => {
  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetTeamById(teamId, { enabled: show && Boolean(teamId) });

  const team =
    (data && typeof data === "object" && !Array.isArray(data)
      ? data.data && typeof data.data === "object" && !Array.isArray(data.data)
        ? data.data
        : data
      : null) || fallback;

  const members = Array.isArray(team?.members) ? team.members : [];
  const vehicle = team?.vehicle && typeof team.vehicle === "object" ? team.vehicle : null;
  const imagePath =
    team?.image || team?.logo || team?.team_image || team?.thumbnail || "";

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Team details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && !team ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : isError && !team ? (
          <p className="text-danger mb-0">
            {getRegistrationApiErrorMessage(error, "Failed to load team.")}
          </p>
        ) : !team ? (
          <p className="text-muted mb-0">Team not found.</p>
        ) : (
          <>
            <div className="d-flex gap-3 align-items-start mb-4">
              <div
                className="d-flex align-items-center justify-content-center overflow-hidden border rounded"
                style={{ width: 72, height: 72, flexShrink: 0 }}
              >
                {imagePath ? (
                  <img
                    src={getJeepRallyMediaUrl(imagePath)}
                    alt={team.team_name || team.name || "Team"}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span className="text-muted small">No image</span>
                )}
              </div>
              <div>
                <h5 className="mb-1">{team.team_name || team.name || "-"}</h5>
                <div className="text-muted small mb-2">
                  ID: <code>{getEntityId(team) || teamId || "-"}</code>
                </div>
                {team.status ? <Badge bg="secondary">{team.status}</Badge> : null}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <DetailRow
                  label="Team number"
                  value={
                    team.team_number === 0 || team.team_number
                      ? String(team.team_number)
                      : team.number || "-"
                  }
                />
                <DetailRow
                  label="Driver"
                  value={resolvePersonName(team.driver_id || team.driver)}
                />
                <DetailRow
                  label="Navigator"
                  value={resolvePersonName(
                    team.navigator_id || team.navigator
                  )}
                />
              </div>
              <div className="col-md-6">
                <DetailRow
                  label="Created"
                  value={formatRegistrationDate(team.createdAt || team.created_at)}
                />
                <DetailRow
                  label="Updated"
                  value={formatRegistrationDate(team.updatedAt || team.updated_at)}
                />
                {team.driver_name ? (
                  <DetailRow label="Driver name" value={team.driver_name} />
                ) : null}
              </div>
            </div>

            {vehicle ? (
              <div className="mt-2">
                <h6 className="mb-3">Vehicle</h6>
                <div className="row">
                  {Object.entries(vehicle).map(([key, value]) => {
                    if (
                      value === null ||
                      value === undefined ||
                      typeof value === "object"
                    ) {
                      return null;
                    }
                    return (
                      <div className="col-md-6" key={key}>
                        <DetailRow
                          label={formatLabel(key)}
                          value={String(value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {members.length ? (
              <div className="mt-3">
                <h6 className="mb-3">Members</h6>
                <ul className="mb-0">
                  {members.map((member, index) => (
                    <li key={getEntityId(member) || index}>
                      {resolvePersonName(member)}
                      {member.role ? ` (${member.role})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TeamDetailModal;
